import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, Drawer, Fade, Paper } from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import MDBox from 'components/MDBox';
import {
  useBulkDeletePromptsMutation,
  useCreatePromptMutation,
  useGetPromptsQuery,
  useUpdatePromptMutation
} from 'services/promptApi';
import './prompt.css';
import PromptDetails from 'pages/prompts/components/PromptsDetails';
import PromptCustomToolbar from 'pages/prompts/components/PromptCustomToolbar';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';

/**
 * Component for managing API keys, including displaying a list of API keys, adding, updating,
 * duplicating, and deleting keys. Uses a DataGrid for listing the keys and a Drawer for editing details.
 *
 * @component
 *
 * @example
 * <Keys />
 *
 * @returns {React.Component} The Keys management component.
 */
export default function Prompts() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const apiRef = useGridApiRef();

  const {
    data: prompts = [],
    error: promptsError,
    isLoading: promptsLoading
  } = useGetPromptsQuery();
  const [bulkDeletePrompt] = useBulkDeletePromptsMutation();
  const [createPrompt] = useCreatePromptMutation();
  const [updatePrompt] = useUpdatePromptMutation();

  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.prompts.table.column.id` }),
      disableColumnMenu: true,
      minWidth: 50,
      maxWidth: 50
    },
    {
      field: 'code',
      headerName: msg({ id: `page.prompts.table.column.code` }),
      minWidth: 350,
      maxWidth: 550
    },
    {
      field: 'type',
      headerName: msg({ id: `page.prompts.table.column.type` }),
      minWidth: 250,
      maxWidth: 250,
      renderCell: (params) => msg({ id: `field.prompt.type.${params.value}.label` })
    },
    {
      field: 'description',
      headerName: msg({ id: `page.prompts.table.column.description` }),
      minWidth: 530,
      maxWidth: 700
    },
    {
      field: 'content',
      headerName: msg({ id: `page.prompts.table.column.content` }),
      minWidth: 200,
      flex: 1
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedPrompt(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedPrompt(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeletePrompt(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.prompts.delete.success' })
          });
          clearPromptSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.prompts.delete.error' })
          });
        });
    }
  };

  const handleDuplicate = () => {
    const toDuplicate = selectedRows[0];
    if (toDuplicate) {
      setSelectedPrompt({ ...toDuplicate, id: null, code: `${toDuplicate.code}_COPY`, key: '' });
      setDetailDrawerOpen(true);
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const selectedRows = rowSelectionModel.map((id) => prompts.find((key) => key.id === id));

  if (promptsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.prompt.fetch.error' })
      });
    } catch {
      // Ignore exception
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            apiRef={apiRef}
            rows={prompts}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: prompts && prompts.length > 0 ? [prompts[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'code', sort: 'asc' }]
              }
            }}
            slots={{ toolbar: PromptCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                onDuplicate: handleDuplicate,
                selectedRows
              }
            }}
            loading={promptsLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <PromptDetails
            prompt={selectedPrompt}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearPromptSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedPrompt(null);
    setSelectedRowId(null);
  }

  function handleCreate(data) {
    createPrompt({ ...data })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.prompts.create.success' })
        });
        clearPromptSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.prompts.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updatePrompt({
      id: data.id,
      promptRequest: { ...data }
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.prompts.update.success' })
        });
        clearPromptSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.prompts.update.error' })
        });
      });
  }
}
